import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Award, ChevronUp, ChevronDown, Minus, Users, Loader2 } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface LeaderboardEntry {
  rank: number;
  user: {
    id: string;
    username: string;
    avatar_url: string;
    level: string;
  };
  points: number;
  change: number;
}

export default function Leaderboard() {
  const [timeFilter, setTimeFilter] = useState<'week' | 'month' | 'all'>('week');
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchLeaderboard();
  }, [timeFilter]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      
      if (!isSupabaseConfigured) {
        // Use mock data when Supabase is not configured
        const mockData = [
          {
            rank: 1,
            user: {
              id: 'user-1',
              username: 'EcoWarrior2024',
              avatar_url: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop',
              level: 'Forest Guardian'
            },
            points: 15420,
            change: 2
          },
          {
            rank: 2,
            user: {
              id: 'user-2',
              username: 'GreenThumb',
              avatar_url: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop',
              level: 'Eco Champion'
            },
            points: 14850,
            change: -1
          },
          {
            rank: 3,
            user: {
              id: 'user-3',
              username: 'PlantLover',
              avatar_url: 'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop',
              level: 'Tree'
            },
            points: 13920,
            change: 1
          }
        ];
        setLeaderboardData(mockData);
        setLoading(false);
        return;
      }

      console.log('Fetching leaderboard from database...');
      
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, avatar_url, level, points')
        .order('points', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error fetching leaderboard:', error);
        setLeaderboardData([]);
        return;
      }

      if (!data || data.length === 0) {
        console.log('No leaderboard data found');
        setLeaderboardData([]);
        return;
      }

      // Transform data to leaderboard format
      const leaderboard = data.map((profile, index) => ({
        rank: index + 1,
        user: {
          id: profile.id,
          username: profile.username,
          avatar_url: profile.avatar_url,
          level: profile.level
        },
        points: profile.points || 0,
        change: 0 // For now, we'll set change to 0. In a real app, you'd track historical rankings
      }));

      setLeaderboardData(leaderboard);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      setLeaderboardData([]);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="h-6 w-6 text-yellow-500" />;
      case 2:
        return <Medal className="h-6 w-6 text-gray-400" />;
      case 3:
        return <Award className="h-6 w-6 text-amber-600" />;
      default:
        return (
          <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
            <span className="text-xs font-bold text-gray-600">{rank}</span>
          </div>
        );
    }
  };

  const getChangeIcon = (change: number) => {
    if (change > 0) {
      return <ChevronUp className="h-4 w-4 text-green-500" />;
    } else if (change < 0) {
      return <ChevronDown className="h-4 w-4 text-red-500" />;
    } else {
      return <Minus className="h-4 w-4 text-gray-400" />;
    }
  };

  const getRankBadgeColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-400 to-yellow-500 border-yellow-300';
      case 2:
        return 'bg-gradient-to-r from-gray-300 to-gray-400 border-gray-300';
      case 3:
        return 'bg-gradient-to-r from-amber-400 to-amber-500 border-amber-300';
      default:
        return 'bg-white border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Eco Leaderboard</h2>
          <p className="text-gray-600 mb-8">
            See how you rank among our global community of eco-warriors
          </p>
        </div>
        
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-emerald-500 mx-auto mb-4" />
            <p className="text-gray-600">Loading leaderboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Eco Leaderboard</h2>
        <p className="text-gray-600 mb-8">
          See how you rank among our global community of eco-warriors
        </p>
        
        {/* Time Filter */}
        <div className="inline-flex bg-gray-100 rounded-lg p-1">
          {[
            { key: 'week', label: 'This Week' },
            { key: 'month', label: 'This Month' },
            { key: 'all', label: 'All Time' }
          ].map((filter) => (
            <button
              key={filter.key}
              onClick={() => setTimeFilter(filter.key as any)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                timeFilter === filter.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {leaderboardData.length === 0 ? (
        <div className="text-center py-12">
          <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No leaderboard data yet</h3>
          <p className="text-gray-600">Complete some missions to see rankings!</p>
        </div>
      ) : (
        <>
          {/* Top 3 Podium */}
          {leaderboardData.length >= 3 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {leaderboardData.slice(0, 3).map((entry, index) => (
                <div
                  key={entry.user.id}
                  className={`${getRankBadgeColor(entry.rank)} rounded-2xl p-6 border-2 ${
                    index === 0 ? 'md:order-2 transform md:scale-105' : 
                    index === 1 ? 'md:order-1' : 'md:order-3'
                  }`}
                >
                  <div className="text-center">
                    <div className="relative mb-4">
                      <img
                        src={entry.user.avatar_url}
                        alt={entry.user.username}
                        className="w-20 h-20 rounded-full mx-auto border-4 border-white shadow-lg object-cover"
                      />
                      <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                        {getRankIcon(entry.rank)}
                      </div>
                    </div>
                    
                    <h3 className="font-bold text-lg text-gray-900 mb-1">
                      {entry.user.username}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      {entry.user.level}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {entry.points.toLocaleString()} pts
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Full Leaderboard */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Full Rankings</h3>
            </div>
            
            <div className="divide-y divide-gray-200">
              {leaderboardData.map((entry) => (
                <div
                  key={entry.user.id}
                  className={`px-6 py-4 hover:bg-gray-50 transition-colors ${
                    entry.user.id === user?.id ? 'bg-emerald-50 border-l-4 border-emerald-500' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        {getRankIcon(entry.rank)}
                        <div className="flex items-center space-x-1">
                          {getChangeIcon(entry.change)}
                          {entry.change !== 0 && (
                            <span className={`text-xs font-medium ${
                              entry.change > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {Math.abs(entry.change)}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <img
                        src={entry.user.avatar_url}
                        alt={entry.user.username}
                        className="w-12 h-12 rounded-full border-2 border-gray-200 object-cover"
                      />
                      
                      <div>
                        <div className="flex items-center space-x-2">
                          <h4 className="font-semibold text-gray-900">
                            {entry.user.username}
                          </h4>
                          {entry.user.id === user?.id && (
                            <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-medium">
                              You
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{entry.user.level}</p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">
                        {entry.points.toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-500">Points</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}